import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DraftReferenceState,
  TestConnectionResult,
} from 'shared/api/referenceOptions';
import { useEphemeralTestConnection } from './useEphemeralTestConnection';
import { getBusyButtonText, getButtonText } from './TestActionFunctions';
import { HoverTooltip } from '../ui/HoverTooltip';
import {
  TestActionButtonLabel,
  TestActionLayout,
  TestActionVerdict,
} from './TestActionLayout';
import { buttonClasses } from '../../../styles/tailwindClassnames';

type Props = {
  action: { value: string; type: string };
  providerType: { actions?: any[]; name?: string; value?: string };
  draftReference?: DraftReferenceState;
  authToken?: string;
  formTitle?: React.ReactNode;
  /** Why this action can't be tested yet. Per screen, because the wording names
   *  the entity being created (a Connection vs a Route). Covers both reasons:
   *  the action isn't auth, or the technology isn't cleared for ephemeral
   *  execution — either way the user has to save first. */
  cannotTestHint: string;
  testid: string;
};

/** Maps the ephemeral result onto the same vocabulary getResponseText produces
 *  for a saved integration; the runner's own message becomes the note. */
function toVerdict(
  result: TestConnectionResult,
  t: (key: string) => string,
): TestActionVerdict {
  if (result.status === 'valid')
    return {
      message: t('Valid Credentials'),
      isSuccess: true,
      note: result.connectedAs
        ? `${t('Connected as')} ${result.connectedAs}`
        : undefined,
    };
  const message =
    result.status === 'invalid'
      ? t('Invalid Credentials')
      : t("Couldn't reach the service — try again");
  return {
    message,
    isSuccess: false,
    note: result.detail,
  };
}

/** Test action for a draft integration — no id yet, so it runs against the
 *  credentials typed into the form via the ephemeral endpoint. Owns only the
 *  gating and the trigger's a11y; everything visual comes from
 *  TestActionLayout, shared with the saved-integration flow. */
export const DraftTestActionButton: React.FC<Props> = ({
  action,
  providerType,
  draftReference,
  authToken,
  formTitle = '',
  cannotTestHint,
  testid,
}) => {
  const { t } = useTranslation();
  const isAuth = action.type === 'auth';
  const test = useEphemeralTestConnection({
    providerType,
    draftReference,
    authToken,
  });
  const focusRing =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-t100 focus-visible:ring-offset-2';
  // No margin: TestActionLayout owns the spacing around the trigger.
  const buttonClass = `${buttonClasses.testAction} ${focusRing}`;

  if (!isAuth || !test.isTestable) {
    const disabledHint = cannotTestHint;
    return (
      <TestActionLayout
        formTitle={formTitle}
        isLoading={false}
        verdict={null}
        testIdPrefix={testid}
        button={
          // The reason shows on hover over the button itself, the same way
          // as the testable-but-incomplete case below. The tooltip's wrapper
          // carries the reason for screen readers, so the button doesn't repeat it.
          <HoverTooltip content={disabledHint}>
            <button
              type="button"
              disabled
              className={buttonClass}
              data-testid={testid}
            >
              {getButtonText(action.value, t)}
            </button>
          </HoverTooltip>
        }
      />
    );
  }

  const label = (
    <TestActionButtonLabel
      isBusy={test.isLoading}
      busyLabel={getBusyButtonText(action.value, t)}
      label={getButtonText(action.value, t)}
    />
  );
  const button = (
    <button
      type="button"
      onClick={test.runTest}
      disabled={!test.canTest}
      aria-busy={test.isLoading}
      aria-label={(!test.canTest && test.disabledReason) || undefined}
      className={buttonClass}
      data-testid={testid}
    >
      {label}
    </button>
  );

  return (
    <TestActionLayout
      formTitle={formTitle}
      isLoading={test.isLoading}
      verdict={test.result ? toVerdict(test.result, t) : null}
      testIdPrefix={testid}
      button={
        test.canTest || test.isLoading ? (
          button
        ) : (
          <HoverTooltip content={test.disabledReason}>{button}</HoverTooltip>
        )
      }
    />
  );
};

export default DraftTestActionButton;
